import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { AdminRecruiterOption, AdminShadowJob, useAdminStore } from '../../../stores/adminStore';
import FullBlockingModal from '../../common/FullBlockingModal';
import { Button } from '../../common/Button';

interface AdminAssignJobToRecruiterModalProps {
  job: AdminShadowJob;
  onClose: () => void;
  onSuccess?: () => void;
}

const getErrorMessage = (err: unknown): string => {
  // supabase-js resolves RPC failures to the parsed PostgREST error object
  // ({ message, details, hint, code }), not an Error instance, so
  // `err instanceof Error` never recovers the message the RPC actually raised.
  const message = (err as { message?: unknown })?.message;
  return typeof message === 'string' ? message : String(err);
};

const getRecruiterOptionLabel = (profile: AdminRecruiterOption): string => {
  const fullName = [profile.first_name, profile.last_name].filter(Boolean).join(' ').trim();
  return fullName || profile.company_name || profile.email || profile.id;
};

const AdminAssignJobToRecruiterModal: React.FC<AdminAssignJobToRecruiterModalProps> = ({
  job,
  onClose,
  onSuccess,
}) => {
  const { t } = useTranslation(['admin', 'common']);
  const { searchRecruiterProfiles, assignJobToRecruiter } = useAdminStore();

  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [results, setResults] = useState<AdminRecruiterOption[]>([]);
  const [loadingResults, setLoadingResults] = useState(false);
  const [assignSearchFailed, setAssignSearchFailed] = useState(false);
  const [selectedProfileId, setSelectedProfileId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const timeout = window.setTimeout(() => setDebouncedSearch(search), 300);
    return () => window.clearTimeout(timeout);
  }, [search]);

  // debouncedSearch starts out as '', so this also covers "fetch on mount
  // with an empty search" (first 10 active recruiters) without waiting for
  // the debounce timer.
  useEffect(() => {
    let cancelled = false;
    setLoadingResults(true);
    setAssignSearchFailed(false);

    searchRecruiterProfiles(debouncedSearch)
      .then(profiles => {
        if (cancelled) return;
        setResults(profiles);
        // If the previously selected recruiter has dropped out of the new
        // results, clear the selection so the confirm button disables again
        // instead of leaving an invisible, still-selected recruiter that the
        // admin could irreversibly assign the job to.
        setSelectedProfileId(current =>
          current && !profiles.some(profile => profile.id === current) ? null : current
        );
      })
      .catch(err => {
        if (cancelled) return;
        console.error('Failed to search recruiter profiles:', err);
        // Non-blocking: the search RPC failing shouldn't prevent the admin
        // from cancelling out of the modal or retrying with a new search term.
        // The translated copy is derived at render time from this boolean flag
        // (rather than calling t() here) so this effect doesn't need `t` in its
        // dependency array — `t`'s identity isn't guaranteed stable across
        // renders, and depending on it here previously caused a render loop
        // that double-fired the search RPC on every mount.
        setAssignSearchFailed(true);
        setResults([]);
        // The list above is unconditionally emptied, so there's no id left
        // worth preserving here (unlike the success-path branch, which only
        // clears the selection if it's no longer present in the new
        // results). Without this, a previously selected recruiter stays
        // "selected" with no visible radio checked, and confirm would still
        // be enabled and irreversibly assign the job to that invisible id.
        setSelectedProfileId(null);
      })
      .finally(() => {
        if (cancelled) return;
        setLoadingResults(false);
      });

    return () => {
      cancelled = true;
    };
  }, [debouncedSearch, searchRecruiterProfiles]);

  // Requires more than just "a recruiter is selected": `search` and
  // `debouncedSearch` diverging means the debounce timer for a newer search
  // term hasn't fired yet, and `loadingResults` means the RPC for that newer
  // term is still in flight. Either way the currently rendered `results`
  // (and therefore the visible radios) may not correspond to the search the
  // admin is looking at, so confirming must stay blocked until results are
  // settled and the selected id is actually among them.
  const canConfirm = Boolean(
    selectedProfileId &&
    !loadingResults &&
    search === debouncedSearch &&
    results.some(result => result.id === selectedProfileId)
  );

  const handleConfirm = async () => {
    // Re-check the full predicate here too, not just via the button's
    // `disabled` attribute: this guards against confirming a stale
    // selection if handleConfirm is ever invoked some other way (e.g. an
    // Enter-key submit) while a debounce/search RPC is still in flight.
    if (!canConfirm || !selectedProfileId) return;

    setSaving(true);
    setError(null);
    try {
      const refreshed = await assignJobToRecruiter(job.id, selectedProfileId);
      toast.success(t('jobModeration.assignSuccess', { ns: 'admin' }));
      if (!refreshed) {
        toast.error(t('jobModeration.assignRefreshError', { ns: 'admin' }));
      }
      onSuccess?.();
      onClose();
    } catch (err) {
      console.error(err);
      const message = getErrorMessage(err);
      if (message.includes('JOB_NOT_ASSIGNABLE')) {
        setError(t('jobModeration.assignErrorNotAssignable', { ns: 'admin' }));
      } else if (message.includes('ASSIGNEE_NOT_ELIGIBLE_RECRUITER')) {
        setError(t('jobModeration.assignErrorNotEligible', { ns: 'admin' }));
      } else if (message.includes('JOB_HAS_QUOTE_ACTIVITY')) {
        setError(t('jobModeration.assignErrorHasQuoteActivity', { ns: 'admin' }));
      } else if (message.includes('ADMIN_NOT_ACTIVE')) {
        setError(t('jobModeration.assignErrorAdminNotActive', { ns: 'admin' }));
      } else {
        setError(t('error.generic', { ns: 'common' }));
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <FullBlockingModal
      innerClassName="md:max-w-[36rem]"
      ariaLabelledBy="admin-assign-recruiter-title"
    >
      <h2 id="admin-assign-recruiter-title" className="text-2xl font-bold text-secondary mb-2">
        {t('jobModeration.assignTitle', { ns: 'admin' })}
      </h2>
      <p className="text-sm font-semibold text-gray-900 mb-1">{job.title}</p>
      {job.shadow_recruiter_company_name ? (
        <p className="text-xs text-gray-500 mb-2">{job.shadow_recruiter_company_name}</p>
      ) : null}
      <p className="text-sm text-gray-600 mb-4">
        {t('jobModeration.assignExplanation', { ns: 'admin' })}
      </p>

      <div className="mb-3">
        <label
          htmlFor="admin-assign-recruiter-search"
          className="block text-sm font-semibold text-gray-700 mb-1"
        >
          {t('jobModeration.assignSearchLabel', { ns: 'admin' })}
        </label>
        <input
          id="admin-assign-recruiter-search"
          type="text"
          value={search}
          onChange={e => {
            // Clear the selection synchronously, before the 300ms debounce
            // timer even starts: without this, selecting Alice then typing
            // "Carol" and clicking Assign during the debounce/loading
            // window would still confirm against Alice's (now invisible)
            // id, since the effect below that clears a dropped-out
            // selection only runs once the *new* results arrive.
            setSearch(e.target.value);
            setSelectedProfileId(null);
          }}
          placeholder={t('jobModeration.assignSearchPlaceholder', { ns: 'admin' })}
          className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
          disabled={saving}
        />
      </div>

      {assignSearchFailed && (
        <p className="mb-2 text-sm text-red-600">
          {t('jobModeration.assignSearchError', { ns: 'admin' })}
        </p>
      )}

      <div className="mb-4 max-h-64 space-y-2 overflow-y-auto">
        {loadingResults ? (
          <p className="text-sm text-gray-500">{t('loading', { ns: 'common' })}</p>
        ) : results.length === 0 ? (
          <p className="text-sm text-gray-500">
            {t('jobModeration.assignNoResults', { ns: 'admin' })}
          </p>
        ) : (
          results.map(profile => (
            <label
              key={profile.id}
              className="flex cursor-pointer items-start gap-3 rounded-lg border border-gray-200 px-3 py-2 hover:border-primary/40"
            >
              <input
                type="radio"
                name="admin-assign-recruiter"
                checked={selectedProfileId === profile.id}
                onChange={() => setSelectedProfileId(profile.id)}
                disabled={saving}
                className="mt-1"
              />
              <div>
                <div className="text-sm font-medium text-gray-900">
                  {getRecruiterOptionLabel(profile)}
                </div>
                {profile.email ? (
                  <div className="text-xs text-gray-500">{profile.email}</div>
                ) : null}
                {profile.company_name ? (
                  <div className="text-xs text-gray-500">{profile.company_name}</div>
                ) : null}
              </div>
            </label>
          ))
        )}
      </div>

      {error && <p className="mt-1 mb-4 text-sm text-red-600">{error}</p>}

      <div className="flex flex-col-reverse gap-3 md:flex-row md:flex-wrap md:justify-end md:gap-4">
        <Button variant="simple-text" onClick={onClose} type="button" disabled={saving}>
          {t('form_actions.cancel', { ns: 'common' })}
        </Button>
        <Button
          variant="primary"
          onClick={handleConfirm}
          type="button"
          disabled={saving || !canConfirm}
        >
          {saving
            ? t('loading', { ns: 'common' })
            : t('jobModeration.confirmAssign', { ns: 'admin' })}
        </Button>
      </div>
    </FullBlockingModal>
  );
};

export default AdminAssignJobToRecruiterModal;
